import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Loader2, Clock, MailWarning, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import EarlyAccessRequestDialog from "./EarlyAccessRequestDialog";

/**
 * Persistent "signup ≠ Early Access" banner shown on the Dashboard,
 * Profile page, and Jobs empty state whenever the current user is a
 * regular signed-in member who has not yet been granted Early Access.
 *
 * The banner is compact (one warm-tone strip) and reuses the same
 * ``/private-tester-requests/me`` state + request dialog as the main
 * Dashboard access card, so the two views never disagree.
 *
 * Renders nothing for admins, approved Early Access members, ephemeral
 * demo visitors, or logged-out visitors. Renders a lightweight "pending"
 * pill instead of the CTA once a request is queued.
 */
export default function EarlyAccessLimitedBanner({ context = "dashboard", className = "" }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const isAlreadyPT = !!user?.is_private_test;
  const isVerified = !!user?.email_verified;

  const [state, setState] = useState({ loading: true, status: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verifyNoticeOpen, setVerifyNoticeOpen] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!user || isAdmin || isAlreadyPT || user?.is_ephemeral) {
      setState({ loading: false, status: isAlreadyPT ? "approved" : "none" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/private-tester-requests/me");
        if (!cancelled) setState({ loading: false, status: data.status });
      } catch {
        if (!cancelled) setState({ loading: false, status: "none" });
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, isAlreadyPT, user]);

  if (!user || isAdmin || isAlreadyPT || user?.is_ephemeral) return null;
  if (state.loading || state.status === "approved") return null;

  const isPending = state.status === "pending";

  const onRequest = () => {
    if (!isVerified) {
      setVerifyNoticeOpen(true);
      toast.error("Please verify your email before requesting Early Access.");
      return;
    }
    setDialogOpen(true);
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/verify-email/resend", {});
      toast.success("Verification email sent — check your inbox in a moment.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "cooldown") {
        toast.info(detail.message || `Please wait ${detail.retry_after_seconds || 60}s before trying again.`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setResending(false);
    }
  };

  // Context-specific copy — same limitation, different framing.
  const COPY = {
    dashboard: {
      title: "Your account is created — Early Access is a separate step.",
      body: "Signing up doesn't grant Early Access. Your profile stays hidden from other members and you can only view demo projects until an admin approves your request.",
    },
    profile: {
      title: "Your profile isn't publicly discoverable yet.",
      body: "Until Early Access is approved, your profile is hidden from other members and doesn't appear in Discover or search results.",
    },
    jobs: {
      title: "Real projects unlock with Early Access.",
      body: "You're browsing the demo marketplace. Request Early Access to view and apply to real beta-read opportunities.",
    },
  };
  const copy = COPY[context] || COPY.dashboard;

  return (
    <div
      className={`rounded-2xl border-2 border-[hsl(var(--sb-gold))/0.6] bg-[hsl(var(--sb-cream-2)/0.65)] px-4 py-4 md:px-5 md:py-5 flex flex-col md:flex-row md:items-center md:flex-wrap gap-3 md:gap-4 ${className}`}
      data-testid={`early-access-limited-banner-${context}`}
      role="status"
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-gold-2))] grid place-items-center shrink-0 text-[hsl(42_60%_96%)]">
        <EyeOff size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm md:text-[15px] font-semibold text-foreground leading-snug">
          {copy.title}
        </p>
        <p className="mt-1 text-xs md:text-sm text-foreground/75 leading-relaxed">
          {copy.body}
        </p>
      </div>
      <div className="shrink-0 self-start md:self-center">
        {isPending ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[hsl(var(--sb-gold-2))] text-[hsl(150_28%_10%)] border border-[hsl(var(--sb-gold-2))] shadow-sm"
            data-testid={`early-access-banner-pending-${context}`}
          >
            <Clock size={14} /> Request Pending
          </span>
        ) : (
          <button
            type="button"
            onClick={onRequest}
            className="btn-primary whitespace-nowrap"
            data-testid={`early-access-banner-request-${context}`}
          >
            Request Early Access
          </button>
        )}
      </div>

      {verifyNoticeOpen && !isVerified && !isPending && (
        <div
          className="w-full md:mt-2 md:basis-full rounded-xl border border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4"
          role="status"
          data-testid={`early-access-banner-verify-${context}`}
        >
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <MailWarning size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
            <p className="text-sm text-foreground leading-snug">
              <span className="font-semibold">Please verify your email</span> before requesting Early Access. We sent a verification link to <span className="font-mono">{user.email}</span>.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={resend}
              disabled={resending}
              className="text-sm font-semibold underline-offset-4 hover:underline text-[hsl(var(--sb-clay))] disabled:opacity-60 whitespace-nowrap"
              data-testid={`early-access-banner-resend-${context}`}
            >
              {resending ? "Sending…" : "Resend verification email"}
            </button>
            <button
              type="button"
              onClick={() => setVerifyNoticeOpen(false)}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted"
              aria-label="Dismiss"
              data-testid={`early-access-banner-verify-dismiss-${context}`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {dialogOpen && (
        <EarlyAccessRequestDialog
          onClose={() => setDialogOpen(false)}
          onSubmitted={() => {
            setState({ loading: false, status: "pending" });
            setDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}
